/**
 * StatePanel - the six honest non-happy-path states every W1 data view can be
 * in (Story #5351 W1-T4). Never a blank page, never a fabricated value —
 * DoD-07 says show StatePanel instead of a synthetic count.
 */
import React from 'react';
import { Loader2, Inbox, HelpCircle, WifiOff, Clock, Lock } from 'lucide-react';
import UnavailableReason from './UnavailableReason';

export type PanelState = 'loading' | 'empty' | 'unknown' | 'unreachable' | 'stale' | 'unauthorised';

const STATE_META: Record<PanelState, { label: string; icon: React.ElementType; iconClassName: string; defaultMessage: string }> = {
  loading: { label: 'Loading', icon: Loader2, iconClassName: 'animate-spin text-sky-500', defaultMessage: 'Loading…' },
  empty: { label: 'Empty', icon: Inbox, iconClassName: 'text-slate-400', defaultMessage: 'Nothing here yet.' },
  unknown: { label: 'Unknown', icon: HelpCircle, iconClassName: 'text-slate-400', defaultMessage: "This state can't be determined right now." },
  unreachable: { label: 'Unreachable', icon: WifiOff, iconClassName: 'text-red-500', defaultMessage: 'Could not reach the source.' },
  stale: { label: 'Stale', icon: Clock, iconClassName: 'text-amber-500', defaultMessage: 'Showing data that may be out of date.' },
  unauthorised: { label: 'Unauthorised', icon: Lock, iconClassName: 'text-red-500', defaultMessage: 'You do not have access to this.' },
};

interface StatePanelProps {
  state: PanelState;
  /** Overrides the default per-state message. */
  message?: string;
  /** Persistent reason (rendered via UnavailableReason) — typically set for unreachable/unauthorised/unknown. */
  reason?: string;
  className?: string;
}

const StatePanel: React.FC<StatePanelProps> = ({ state, message, reason, className }) => {
  const meta = STATE_META[state];
  const Icon = meta.icon;
  return (
    <div
      data-state={state}
      className={`flex flex-col items-center justify-center gap-2 py-10 px-4 text-center ${className ?? ''}`}
    >
      <Icon className={`w-8 h-8 ${meta.iconClassName}`} aria-hidden="true" />
      <p className="text-sm font-semibold text-gray-700">{meta.label}</p>
      <p className="text-sm text-gray-500 max-w-sm">{message ?? meta.defaultMessage}</p>
      {reason && <UnavailableReason reason={reason} className="mt-1 text-left" />}
    </div>
  );
};

export default StatePanel;
